/* eslint-disable-off functional/immutable-data */
import { format, parseISO } from 'date-fns';
import * as Excel from 'exceljs';
import { saveAs } from 'file-saver';
import gql from 'graphql-tag';
import get from 'lodash/get';
import replace from 'lodash/replace';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Modal, ProgressBar } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createModal } from 'react-modal-promise';

import { FlexSpinner } from '.';

const fileExtension = '.xlsx';

const GetReportTablesDocument = gql`
  query getReportTables(
    $where: String
    $offset: Int
    $limit: Int
    $order: String
  ) {
    getReportTables(
      where: $where
      offset: $offset
      limit: $limit
      order: $order
    ) {
      data {
        tableName
        iNo
        isActive
        fieldName
        fieldType
        fieldFormat
        fieldDescription
      }
      recordCount
    }
  }
`;

// Καθορισμός Excel Column
export type ColumnDef = {
  readonly fieldTitle: string;
  readonly fieldName: string; // Ουσιαστικά είναι το (key) path στο object
  readonly fieldType: string; // 'BOOL' | 'DATETIME' | 'TEXT';
  readonly fieldFind?: string; // Για να κάνει findIndex μέσα σε Array που δεν ξέρουμε τον αριθμό
};

function addHeaders(ws: Excel.Worksheet, columnsDef: readonly ColumnDef[]) {
  // eslint-disable-off-next-line-off functional/prefer-readonly-type
  const wsColumns: Array<any> = [];
  columnsDef.forEach((column) => {
    const wsColumn = {
      header: column.fieldTitle,
      key: column.fieldName,
      width: column.fieldTitle.length < 16 ? 16 : column.fieldTitle.length,
    };
    wsColumns.push(wsColumn);
  });
  ws.columns = [...wsColumns];
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).alignment = { vertical: 'top' };
}

// Προσθήκη εγγραφής ως νέας γραμμης στο excel.worksheet
function addRecord(
  ws: Excel.Worksheet,
  record,
  columnsDef: readonly ColumnDef[],
  t
) {
  const wsRow = {};
  columnsDef.forEach((column) => {
    const fieldName = column.fieldName;
    const fieldType = column.fieldType;
    let value;
    if (!!column.fieldFind) {
      const numberFromFind = eval(column.fieldFind);
      value = get(
        record,
        replace(fieldName, new RegExp(`numberFromFind\\d`), numberFromFind)
      );
    } else {
      value = get(record, fieldName); // Lodash get  // get fieldName key from record object
    }
    if (fieldType === 'BOOL') wsRow[fieldName] = value ? t('YES') : t('NO');
    else if (fieldType === 'DATETIME')
      wsRow[fieldName] = value
        ? format(parseISO(value), 'dd/MM/yyyy hh:mm')
        : '';
    else if (fieldType === 'DATE')
      wsRow[fieldName] = value ? format(parseISO(value), 'dd/MM/yyyy') : '';
    else {
      wsRow[fieldName] = value;
    }
  });
  const r = ws.addRow(wsRow, '');
  r.alignment = { vertical: 'top', wrapText: true };
}

function useDummyLazyQuery() {
  return [null, { loading: null, data: null }];
}

/*
   Κάνει fallBack: recordsData => getLazyQuery
                   Δεδομένα που του περνάς => Query που του δίνεις

   Κάνει fallBack: columnsDef => tableName
                   Πεδία που του περνάς => Πεδία απο report_tables πίνακα στην βάση δεδομένων
*/

type Props<D extends object = {}> = {
  readonly recordsData?: any; // Προαιρετικός πίνακας Δεδομένων, αλλιώς τραβάει απο το LazyQuery
  readonly getLazyQuery?: (any) => any; // Συνάρτηση που επιστρέφει LazyQuery
  //
  readonly tableName?: string; // Διάβασε τα excel columns definitions απο τον πίνακα report_tables για το tableName
  readonly columnsDef?: readonly ColumnDef[]; // Προαιρετικός πίνακας με column definitions, αλλιώς διαβάζει απο τον reports_table πινακα
  //
  readonly filterVariables?: Record<string, any>;
  //
  readonly open: boolean; // required for react-modal-promise
  readonly close: (result: boolean) => void; // required for react-modal-promise
  //
  readonly exportFileName?: string; // Προαιρετικό Όνομα αρχείου που θα δημιουργηθεί
  // eslint-disable-off-next-line-off @typescript-eslint/ban-types
  readonly apolloClient: any;
};

type ExportXlsModalOptions = Omit<Props, 'open' | 'close'>;

/* --------------------------
   Component
   -------------------------- */
const ExportXlsModal = ({
  getLazyQuery = useDummyLazyQuery,
  filterVariables,
  tableName,
  columnsDef: columnsDef0 = [],
  open,
  recordsData,
  close,
  exportFileName,
  apolloClient,
}: Props) => {
  const [columnsDef, setColumnsDef] = useState(columnsDef0);
  const { t, i18n } = useTranslation();
  //
  //-------------------- Body -----------------------------------------------
  //
  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const pageSize = 100; // Μέγεθος πακέτου δεδομένων
  const maxNoOfRecords = 50000; // Όριο εγγραφών/γραμμών στο αρχείο  <=============== MAXIMUM RECORDS TO FETCH
  const [pageIndex, setPageIndex] = useState(0);
  const offset = pageSize * pageIndex;
  // refs (wb & ws)
  const wb = useRef<Excel.Workbook>(null);
  const ws = useRef<Excel.Worksheet>(null);
  //const reportTables = useRef<Array<ReportTable>>(null);
  const recordsCount = useRef<number>(null);

  // memorize variables for data query
  //
  const variables = useMemo(
    () => ({ ...filterVariables, offset, limit: pageSize }),
    [filterVariables, offset]
  );

  // query Data
  //
  const [get, { data }] = getLazyQuery({});

  /* ---------- First Time Only ---------------- */
  /* Φέρε Columns απο report_tables πίνακα στην
     βάση δεδομένων, ΑΝ ΧΡΕΙΑΖΕΤΑΙ
                                                 */
  const onGetReportColumns = useCallback(
    (dataReportTables) => {
      console.log('[ExportXlsModal] ReportTables:', dataReportTables);
      //reportTables.current = [...dataReportTables.getReportTables.data];
      if (!columnsDef || columnsDef.length === 0) {
        // Συμπλήρωσε τον πίνακα columnsDef απο τα records
        const recs = dataReportTables.getReportTables.data;
        const newColumnsDef = columnsDef.slice();
        recs
          .filter((rec) => rec.isActive)
          .map((rec) => {
            newColumnsDef.push({
              fieldName: rec.fieldName,
              fieldType: rec.fieldType,
              fieldTitle: rec.fieldDescription,
            });
          });
        setColumnsDef(newColumnsDef);
      }
      //Φέρε τα δεδομένα (1o πακέτο)
      if (!recordsData) {
        get({ variables, fetchPolicy: 'no-cache' });
        setPageIndex(pageIndex + 1);
      }
    },
    [columnsDef, get, pageIndex, recordsData, variables]
  );

  // const [getReportColumns, { data: dataReportTables }] = useLazyQuery(
  //   GetReportTablesDocument,
  //   {
  //     onCompleted: onGetReportColumns,
  //     client: apolloClient,
  //     fetchPolicy: 'cache-and-network',
  //   }
  // );
  useEffect(() => {
    if (!columnsDef || columnsDef.length === 0) {
      console.log('[ExportXlsModal] Fetch Column Definition from Report_Table');
      apolloClient
        .query({
          query: GetReportTablesDocument,
          fetchPolicy: 'network-only',
          variables: { where: JSON.stringify({ tableName: tableName }) },
        })
        .then((dataReportTables) => onGetReportColumns(dataReportTables));

      // getReportColumns({
      //   variables: { where: JSON.stringify({ tableName: tableName }) },
      // });
    } else {
      //Φέρε τα δεδομένα (1o πακέτο)
      if (!recordsData) {
        get({ variables, fetchPolicy: 'no-cache' });
        setPageIndex(pageIndex + 1);
      }
    }
    // []
  }, []);

  // * New Records Effect! (onNewData)
  useEffect(() => {
    if (recordsData) {
      const records = recordsData;
      const count = recordsData.length;
      if (!wb.current) wb.current = new Excel.Workbook();
      if (!ws.current && wb.current) {
        ws.current = wb.current.addWorksheet('Records');
        addHeaders(ws.current, columnsDef);
      }
      if (!recordsCount.current) recordsCount.current = count;
      records.forEach((record) => addRecord(ws.current, record, columnsDef, t));
      const recordsFetchedCount = records.length;
      if (
        recordsFetchedCount >= count ||
        recordsFetchedCount >= maxNoOfRecords
      ) {
        // Τέλος κλείσε το modal & αποθήκευσε το αρχείο excel
        close(true);
        wb.current.xlsx.writeBuffer().then((data) => {
          console.log('saving');
          const blob = new Blob([data], { type: fileType });

          const fileName = exportFileName
            ? exportFileName + fileExtension
            : 'export-' + (tableName ? tableName : 'table') + fileExtension;
          saveAs(blob, fileName);
        });
      }
    } else if (data) {
      // ΓΙΑ ΝΑ ΤΟ ΚΑΝΩ ΓΕΝΙΚΟ ΒΡΙΣΚΩ ΤΟ propertyName
      const propNameData = Object.getOwnPropertyNames(data)[0];
      const records = data[propNameData]['data'];
      const count = data[propNameData]['recordCount'];
      //console.log('[ExportXlsModal] New Records:', data, propNameData, records, count);
      // initialize wb and ws

      if (!wb.current) wb.current = new Excel.Workbook();
      if (!ws.current && wb.current) {
        ws.current = wb.current.addWorksheet('Records');
        addHeaders(ws.current, columnsDef);
      }

      if (!recordsCount.current) recordsCount.current = count;

      // Πρόσθεσε τις τρέχουσες εγγραφές στο buffer
      records.forEach((record) => addRecord(ws.current, record, columnsDef, t));

      const recordsFetchedCount = offset + records.length;
      // Όριο εγγραφών/γραμμών στο αρχείο
      if (
        (recordsFetchedCount >= count && offset > count) ||
        recordsFetchedCount >= maxNoOfRecords
      ) {
        // Τέλος κλείσε το modal & αποθήκευσε το αρχείο excel
        close(true);
        wb.current.xlsx.writeBuffer().then((data) => {
          const blob = new Blob([data], { type: fileType });

          const fileName = exportFileName
            ? exportFileName + fileExtension
            : 'export-' + (tableName ? tableName : 'table') + fileExtension;
          console.log('export-xls save file', fileName);
          saveAs(blob, fileName);
        });
      } else {
        setPageIndex(pageIndex + 1);
        //Φέρε τα δεδομένα (σε πακέτα)

        get({ variables, fetchPolicy: 'no-cache' });
      }
    }

    // }, [data, recordsData, pageIndex]);
  }, [data]);
  //   // eslint-disable-off-next-line-off react-hooks/exhaustive-deps
  return (
    <Modal show={open} centered backdrop="static">
      <Modal.Header>
        <Modal.Title>
          {i18n.language === 'gr'
            ? t('Εξαγωγή σε Excel')
            : t('Exporting to Excel')}
        </Modal.Title>
      </Modal.Header>
      <FlexSpinner />
      {/* {recordsCount.current && (
        <Modal.Body>
          Εγγραφές: {(pageSize * pageIndex).toLocaleString()} / {Math.min(recordsCount.current, maxNoOfRecords).toLocaleString()}
          <ProgressBar now={(100 * pageSize * pageIndex) / Math.min(recordsCount.current, maxNoOfRecords)} />
        </Modal.Body>
      )} */}
      {recordsCount.current ? (
        <Modal.Body>
          <div className="mb-2">
            {i18n.language === 'gr' ? t('Εγγραφές') : t('Records')}:{' '}
            {(pageSize * pageIndex > recordsCount.current
              ? recordsCount.current
              : pageSize * pageIndex
            ).toLocaleString()}{' '}
            / {Math.min(recordsCount.current, maxNoOfRecords).toLocaleString()}
          </div>
          <ProgressBar
            now={
              (100 * pageSize * pageIndex) /
              Math.min(recordsCount.current, maxNoOfRecords)
            }
          />
        </Modal.Body>
      ) : (
        <Modal.Body>
          <div className="mb-2">{t('Records')}: -/-</div>
          <ProgressBar now={0} />
        </Modal.Body>
      )}
      <Modal.Footer>
        <Button variant="secondary" onClick={() => close(false)}>
          {t('CANCEL')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Μετατρέπει το Modal σε Promise<boolean>
// πρέπει το component να έχει open + close parameters
export const exportXlsModal = (createModal as any)(
  ExportXlsModal as React.ComponentType<any>
);
